import axios from 'axios';
import { API_BASE_URL } from './config';

// Interfaces
export interface Client {
  _id: string;
  companyName: string;
  contactPerson: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    title: string;
  };
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  billingAddress?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  taxId?: string;
  website?: string;
  industry?: string;
  clientType: 'individual' | 'business' | 'government' | 'nonprofit';
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  portalAccess: {
    enabled: boolean;
    username?: string;
    lastLogin?: string;
    permissions: ClientPermission[];
  };
  projects: string[]; // Project IDs
  documents: ClientDocument[];
  communications: ClientCommunication[];
  preferences: ClientPreferences;
  createdBy: {
    _id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ClientPermission {
  resource: 'projects' | 'documents' | 'invoices' | 'schedules' | 'photos' | 'reports';
  actions: ('view' | 'download' | 'comment' | 'approve')[];
}

export interface ClientDocument {
  _id: string;
  name: string;
  type: 'contract' | 'proposal' | 'invoice' | 'report' | 'photo' | 'drawing' | 'other';
  category: string;
  description?: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  isPublic: boolean;
  requiresApproval: boolean;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  approvedBy?: {
    _id: string;
    name: string;
    date: string;
  };
  uploadedBy: {
    _id: string;
    name: string;
  };
  uploadedAt: string;
  expiresAt?: string;
  downloadCount: number;
  lastDownloaded?: string;
}

export interface ClientCommunication {
  _id: string;
  type: 'email' | 'message' | 'call' | 'meeting' | 'note';
  subject: string;
  content: string;
  direction: 'inbound' | 'outbound';
  status: 'draft' | 'sent' | 'delivered' | 'read' | 'replied';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  attachments: {
    name: string;
    url: string;
    size: number;
  }[];
  relatedProject?: {
    _id: string;
    name: string;
  };
  participants: {
    _id: string;
    name: string;
    email: string;
    role: 'client' | 'staff';
  }[];
  createdBy: {
    _id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ClientPreferences {
  notifications: {
    email: boolean;
    sms: boolean;
    projectUpdates: boolean;
    invoiceReminders: boolean;
    documentSharing: boolean;
  };
  communication: {
    preferredMethod: 'email' | 'phone' | 'portal';
    timezone: string;
    language: string;
  };
  portal: {
    theme: 'light' | 'dark';
    dashboardLayout: string;
    defaultView: 'projects' | 'documents' | 'invoices';
  };
}

export interface ClientProject {
  _id: string;
  name: string;
  description: string;
  status: 'planning' | 'active' | 'on-hold' | 'completed' | 'cancelled';
  progress: number;
  startDate: string;
  endDate?: string;
  estimatedCompletion?: string;
  budget: {
    total: number;
    spent: number;
    remaining: number;
  };
  milestones: ProjectMilestone[];
  recentUpdates: ProjectUpdate[];
  photos: {
    _id: string;
    url: string;
    caption: string;
    date: string;
  }[];
  documents: ClientDocument[];
}

export interface ProjectMilestone {
  _id: string;
  name: string;
  description: string;
  dueDate: string;
  status: 'pending' | 'in-progress' | 'completed' | 'overdue';
  completedDate?: string;
  progress: number;
}

export interface ProjectUpdate {
  _id: string;
  title: string;
  content: string;
  type: 'progress' | 'milestone' | 'issue' | 'completion' | 'delay';
  date: string;
  author: {
    _id: string;
    name: string;
  };
  attachments?: {
    name: string;
    url: string;
  }[];
}

export interface ClientInvoice {
  _id: string;
  invoiceNumber: string;
  projectId: string;
  projectName: string;
  amount: number;
  tax: number;
  total: number;
  status: 'draft' | 'sent' | 'viewed' | 'paid' | 'overdue' | 'cancelled';
  dueDate: string;
  paidDate?: string;
  items: InvoiceItem[];
  paymentMethod?: string;
  notes?: string;
  createdAt: string;
}

export interface InvoiceItem {
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

export interface ClientFilters {
  status?: string;
  clientType?: string;
  industry?: string;
  dateFrom?: string;
  dateTo?: string;
  hasPortalAccess?: boolean;
}

export interface ClientStats {
  totalClients: number;
  activeClients: number;
  clientsWithPortalAccess: number;
  newClientsThisMonth: number;
  clientsByType: {
    type: string;
    count: number;
  }[];
  clientsByStatus: {
    status: string;
    count: number;
  }[];
  portalUsage: {
    totalLogins: number;
    uniqueUsers: number;
    averageSessionDuration: number;
  };
  recentActivity: {
    date: string;
    logins: number;
    documentDownloads: number;
  }[];
}

// Axios instance with auth token
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Client Portal Service
class ClientPortalService {
  // Client Management
  async getClients(filters?: ClientFilters): Promise<Client[]> {
    const response = await api.get('/clients', { params: filters });
    // Backend returns { success, data, pagination }; unwrap the data array
    return response.data?.data ?? [];
  }

  async getClient(id: string): Promise<Client> {
    const response = await api.get(`/clients/${id}`);
    return response.data;
  }

  async createClient(client: Omit<Client, '_id' | 'createdAt' | 'updatedAt' | 'createdBy'>): Promise<Client> {
    const response = await api.post('/clients', client);
    return response.data;
  }

  async updateClient(id: string, client: Partial<Client>): Promise<Client> {
    const response = await api.put(`/clients/${id}`, client);
    return response.data;
  }

  async deleteClient(id: string): Promise<void> {
    await api.delete(`/clients/${id}`);
  }

  // Portal Access Management
  async enablePortalAccess(clientId: string, permissions: ClientPermission[]): Promise<{ username: string; temporaryPassword: string }> {
    const response = await api.post(`/clients/${clientId}/portal-access`, { permissions });
    return response.data;
  }

  async disablePortalAccess(clientId: string): Promise<void> {
    await api.delete(`/clients/${clientId}/portal-access`);
  }

  async updatePortalPermissions(clientId: string, permissions: ClientPermission[]): Promise<Client> {
    const response = await api.put(`/clients/${clientId}/portal-permissions`, { permissions });
    return response.data;
  }

  async resetPortalPassword(clientId: string): Promise<{ temporaryPassword: string }> {
    const response = await api.post(`/clients/${clientId}/reset-password`);
    return response.data;
  }

  // Client Projects
  async getClientProjects(clientId: string): Promise<ClientProject[]> {
    // Backend does not expose /clients/:id/projects; adapt using /projects
    // Special-case 'all' to fetch all projects for the user's company
    const mapToClientProject = (p: any): ClientProject => {
      const total = Number(p?.budget?.totalBudget ?? 0);
      const spent = Number(p?.budget?.costToDate ?? p?.budget?.actualCost ?? 0);
      const remaining = Math.max(total - spent, 0);
      const start = p?.timeline?.startDate ? new Date(p.timeline.startDate).toISOString() : '';
      const end = p?.timeline?.endDate ? new Date(p.timeline.endDate).toISOString() : '';
      const progress = Number(p?.progress?.overallCompletion ?? 0);
      const milestones = Array.isArray(p?.timeline?.milestones)
        ? p.timeline.milestones.map((m: any) => ({
            _id: m?._id ?? '',
            name: m?.name ?? '',
            description: m?.description ?? '',
            dueDate: m?.targetDate ? new Date(m.targetDate).toISOString() : '',
            status: (m?.status === 'in_progress' ? 'in-progress' : (m?.status ?? 'pending')) as any,
            completedDate: m?.actualDate ? new Date(m.actualDate).toISOString() : undefined,
            progress: Number(m?.completionPercentage ?? 0),
          }))
        : [];

      return {
        _id: p?._id ?? '',
        name: p?.name ?? '',
        description: p?.description ?? '',
        status: (p?.status ?? 'planning') as any,
        progress,
        startDate: start,
        endDate: end || undefined,
        estimatedCompletion: end || undefined,
        budget: { total, spent, remaining },
        milestones,
        recentUpdates: [],
        photos: [],
        documents: Array.isArray(p?.documents)
          ? p.documents.map((d: any) => ({
              _id: d?._id ?? '',
              name: d?.name ?? '',
              type: (d?.type ?? 'other') as any,
              category: d?.type ?? 'other',
              description: d?.description ?? '',
              fileUrl: d?.filePath ?? '',
              fileName: d?.name ?? '',
              fileSize: Number(d?.fileSize ?? 0),
              mimeType: d?.mimeType ?? '',
              isPublic: true,
              requiresApproval: false,
              uploadedBy: { _id: String(d?.uploadedBy ?? ''), name: '' },
              uploadedAt: d?.uploadedAt ? new Date(d.uploadedAt).toISOString() : new Date().toISOString(),
              downloadCount: 0,
            }))
          : [],
      };
    };

    if (clientId === 'all') {
      const response = await api.get('/projects', { params: { page: 1, limit: 250, sortBy: 'createdAt', sortOrder: 'desc' } });
      const list = response.data?.data ?? [];
      return list.map(mapToClientProject);
    }

    // Attempt to resolve specific client by synthetic key "name|email" or plain name
    const [namePart, emailPart] = String(clientId).split('|');
    const search = namePart || clientId;
    const resp = await api.get('/projects', { params: { search, page: 1, limit: 250 } });
    const all = resp.data?.data ?? [];
    const filtered = all.filter((p: any) => {
      const c = p?.client || {};
      const matchName = c?.name && String(c.name).trim().toLowerCase() === String(namePart || clientId).trim().toLowerCase();
      const matchEmail = emailPart ? (String(c?.email || '').trim().toLowerCase() === String(emailPart).trim().toLowerCase()) : true;
      return matchName && matchEmail;
    });
    return filtered.map(mapToClientProject);
  }

  async getClientProject(clientId: string, projectId: string): Promise<ClientProject> {
    const response = await api.get(`/clients/${clientId}/projects/${projectId}`);
    return response.data;
  }

  async addProjectToClient(clientId: string, projectId: string): Promise<void> {
    await api.post(`/clients/${clientId}/projects`, { projectId });
  }

  async removeProjectFromClient(clientId: string, projectId: string): Promise<void> {
    await api.delete(`/clients/${clientId}/projects/${projectId}`);
  }

  // Document Management
  async getClientDocuments(clientId: string, type?: string): Promise<ClientDocument[]> {
    const params = type ? { type } : {};
    const response = await api.get(`/clients/${clientId}/documents`, { params });
    return response.data;
  }

  async uploadClientDocument(clientId: string, file: File, metadata: Partial<ClientDocument>): Promise<ClientDocument> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('metadata', JSON.stringify(metadata));
    
    const response = await api.post(`/clients/${clientId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteClientDocument(clientId: string, documentId: string): Promise<void> {
    await api.delete(`/clients/${clientId}/documents/${documentId}`);
  }

  async approveDocument(clientId: string, documentId: string, approved: boolean, comments?: string): Promise<ClientDocument> {
    const response = await api.post(`/clients/${clientId}/documents/${documentId}/approve`, {
      approved,
      comments,
    });
    return response.data;
  }

  // Communication Management
  async getClientCommunications(clientId: string, type?: string): Promise<ClientCommunication[]> {
    const params = type ? { type } : {};
    const response = await api.get(`/clients/${clientId}/communications`, { params });
    return response.data;
  }

  async createCommunication(clientId: string, communication: Omit<ClientCommunication, '_id' | 'createdAt' | 'updatedAt' | 'createdBy'>): Promise<ClientCommunication> {
    const response = await api.post(`/clients/${clientId}/communications`, communication);
    return response.data;
  }

  async updateCommunication(clientId: string, communicationId: string, communication: Partial<ClientCommunication>): Promise<ClientCommunication> {
    const response = await api.put(`/clients/${clientId}/communications/${communicationId}`, communication);
    return response.data;
  }

  async deleteCommunication(clientId: string, communicationId: string): Promise<void> {
    await api.delete(`/clients/${clientId}/communications/${communicationId}`);
  }

  // Invoice Management
  async getClientInvoices(clientId: string, status?: string): Promise<ClientInvoice[]> {
    const params = status ? { status } : {};
    const response = await api.get(`/clients/${clientId}/invoices`, { params });
    return response.data;
  }

  async getClientInvoice(clientId: string, invoiceId: string): Promise<ClientInvoice> {
    const response = await api.get(`/clients/${clientId}/invoices/${invoiceId}`);
    return response.data;
  }

  async markInvoiceAsPaid(clientId: string, invoiceId: string, paymentDetails: any): Promise<ClientInvoice> {
    const response = await api.post(`/clients/${clientId}/invoices/${invoiceId}/pay`, paymentDetails);
    return response.data;
  }

  // Client Preferences
  async getClientPreferences(clientId: string): Promise<ClientPreferences> {
    const response = await api.get(`/clients/${clientId}/preferences`);
    return response.data;
  }

  async updateClientPreferences(clientId: string, preferences: Partial<ClientPreferences>): Promise<ClientPreferences> {
    const response = await api.put(`/clients/${clientId}/preferences`, preferences);
    return response.data;
  }

  // Portal Authentication (for client-side use)
  async clientLogin(username: string, password: string): Promise<{ token: string; client: Client }> {
    const response = await api.post('/client-portal/login', { username, password });
    return response.data;
  }

  async clientLogout(): Promise<void> {
    await api.post('/client-portal/logout');
  }

  async changeClientPassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post('/client-portal/change-password', { currentPassword, newPassword });
  }

  // Dashboard Data
  async getClientDashboard(clientId: string): Promise<any> {
    const response = await api.get(`/clients/${clientId}/dashboard`);
    return response.data;
  }

  async getPortalActivity(clientId: string, limit: number = 20): Promise<any[]> {
    const response = await api.get(`/clients/${clientId}/activity`, { params: { limit } });
    return response.data;
  }

  // Bulk Operations
  async bulkUpdateClients(clientIds: string[], updates: Partial<Client>): Promise<void> {
    await api.patch('/clients/bulk-update', { clientIds, updates });
  }

  async bulkDeleteClients(clientIds: string[]): Promise<void> {
    await api.delete('/clients/bulk-delete', { data: { clientIds } });
  }

  // Statistics and Reports
  async getClientStats(dateFrom?: string, dateTo?: string): Promise<ClientStats> {
    const params: any = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    
    const response = await api.get('/clients/stats', { params });
    return response.data;
  }

  async getPortalUsageReport(dateFrom?: string, dateTo?: string): Promise<any> {
    const params: any = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    
    const response = await api.get('/clients/portal-usage-report', { params });
    return response.data;
  }

  // Search
  async searchClients(query: string): Promise<Client[]> {
    const response = await api.get('/clients/search', { params: { q: query } });
    return response.data;
  }

  // Export
  async exportClients(filters?: ClientFilters, format: 'csv' | 'excel' | 'json' = 'csv'): Promise<Blob> {
    const response = await api.get('/clients/export', {
      params: { ...filters, format },
      responseType: 'blob',
    });
    return response.data;
  }

  async exportClientData(clientId: string, format: 'pdf' | 'json' = 'pdf'): Promise<Blob> {
    const response = await api.get(`/clients/${clientId}/export`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  }
}

export const clientPortalService = new ClientPortalService();
export default clientPortalService;